/**
 * Allowed email domains ("join by work email", like Slack's approved domains): admins list company domains, and a
 * signed-in person whose verified address is on one of them can join the workspace as a member without an invite.
 *
 * Pure, shared by the server (validation, matching) and every client (the settings editor, the join list). Matching
 * is exact on the whole domain (`acme.com` doesn't admit `eu.acme.com`); public mail providers can't be listed;
 * Apple "Hide My Email" relay addresses, placeholder addresses and unverified addresses never match.
 */
import { isPlaceholderEmail, isPrivateRelayEmail } from "./signInMethods.ts";

/** Domains one workspace may list. */
export const MAX_EMAIL_DOMAINS = 10;
/** Longest domain name (RFC 1035). */
export const MAX_DOMAIN_LENGTH = 253;

/** Why a domain can't be listed. */
export type EmailDomainProblem = "invalid" | "public" | "tooMany" | "duplicate";

/**
 * Free/consumer mail providers: anyone can get an address there, so listing one would open the workspace to
 * everyone. Lower case, exact domains.
 */
export const PUBLIC_EMAIL_DOMAINS: readonly string[] = [
  "gmail.com", "googlemail.com",
  "outlook.com", "hotmail.com", "live.com", "msn.com", "passport.com", "outlook.com.br", "hotmail.com.br", "live.com.br",
  "hotmail.co.uk", "hotmail.fr", "hotmail.es", "hotmail.it", "hotmail.de", "live.co.uk",
  "yahoo.com", "yahoo.com.br", "yahoo.co.uk", "yahoo.fr", "yahoo.es", "yahoo.it", "yahoo.de", "yahoo.co.jp", "ymail.com", "rocketmail.com",
  "icloud.com", "me.com", "mac.com",
  "proton.me", "protonmail.com", "protonmail.ch", "pm.me",
  "aol.com", "aim.com",
  "gmx.com", "gmx.net", "gmx.de", "web.de",
  "mail.com", "email.com", "zoho.com", "zohomail.com", "yandex.com", "yandex.ru", "mail.ru", "inbox.ru", "list.ru", "bk.ru",
  "qq.com", "163.com", "126.com", "sina.com", "naver.com", "daum.net",
  "tutanota.com", "tutanota.de", "tuta.io", "tuta.com", "fastmail.com", "fastmail.fm", "hey.com", "duck.com",
  "uol.com.br", "bol.com.br", "terra.com.br", "ig.com.br", "globo.com", "globomail.com", "r7.com",
  "comcast.net", "verizon.net", "att.net", "sbcglobal.net", "btinternet.com", "orange.fr", "free.fr", "laposte.net",
  "privaterelay.appleid.com",
];

const PUBLIC = new Set(PUBLIC_EMAIL_DOMAINS);

/** A public mail provider (exact domain, case-insensitive). */
export function isPublicEmailDomain(domain: string): boolean {
  return PUBLIC.has(domain.trim().toLowerCase());
}

function validLabel(label: string): boolean {
  if (label.length < 1 || label.length > 63) return false;
  if (label.startsWith("-") || label.endsWith("-")) return false;
  for (let i = 0; i < label.length; i++) {
    const c = label.charCodeAt(i);
    const ok = (c >= 0x30 && c <= 0x39) || (c >= 0x61 && c <= 0x7a) || c === 0x2d;
    if (!ok) return false;
  }
  return true;
}

/**
 * The canonical form of what an admin typed: trimmed, lower case, without a leading "@" or a trailing dot. ASCII
 * only (internationalized domains in their xn-- form). Undefined when it isn't a domain name with at least two
 * labels and an alphabetic (or xn--) top-level label.
 */
export function normalizeEmailDomain(input: string): string | undefined {
  let d = input.trim().toLowerCase();
  if (d.startsWith("@")) d = d.slice(1);
  if (d.endsWith(".")) d = d.slice(0, -1);
  if (d === "" || d.length > MAX_DOMAIN_LENGTH) return undefined;
  const labels = d.split(".");
  if (labels.length < 2 || !labels.every(validLabel)) return undefined;
  const tld = labels[labels.length - 1]!;
  if (!(tld.startsWith("xn--") || (tld.length >= 2 && /^[a-z]+$/.test(tld)))) return undefined;
  return d;
}

/** Why this single domain can't be listed (undefined: it can). */
export function emailDomainProblem(input: string): EmailDomainProblem | undefined {
  const d = normalizeEmailDomain(input);
  if (d === undefined) return "invalid";
  if (isPublicEmailDomain(d)) return "public";
  return undefined;
}

/** Splits the settings field (commas, semicolons, spaces, new lines) into entries; empty entries dropped. */
export function parseDomainList(text: string): string[] {
  return text.split(/[\s,;]+/).filter((s) => s !== "");
}

export type DomainListResult = {
  /** Normalized, de-duplicated, in the order typed. */
  domains: string[];
  /** Entries that can't be listed, in the order typed (the first duplicate stays valid; later ones are problems). */
  problems: { input: string; problem: EmailDomainProblem }[];
};

/** Validates the whole list an admin saves: every entry, duplicates and the {@link MAX_EMAIL_DOMAINS} cap. */
export function validateDomainList(inputs: string[]): DomainListResult {
  const domains: string[] = [];
  const problems: DomainListResult["problems"] = [];
  for (const input of inputs) {
    const problem = emailDomainProblem(input);
    if (problem !== undefined) {
      problems.push({ input, problem });
      continue;
    }
    const d = normalizeEmailDomain(input)!;
    if (domains.includes(d)) problems.push({ input, problem: "duplicate" });
    else if (domains.length >= MAX_EMAIL_DOMAINS) problems.push({ input, problem: "tooMany" });
    else domains.push(d);
  }
  return { domains, problems };
}

/** The domain part of an address, lower case (undefined when it has none). */
export function emailDomainOf(email: string | null | undefined): string | undefined {
  if (typeof email !== "string") return undefined;
  const e = email.trim().toLowerCase();
  const at = e.lastIndexOf("@");
  if (at <= 0 || at === e.length - 1) return undefined;
  return normalizeEmailDomain(e.slice(at + 1));
}

/**
 * The domain this address may join with: a verified, real address (not an Apple relay or a placeholder). Undefined
 * when the address can't be used to join by domain at all.
 */
export function joinDomainFor(email: string | null | undefined, emailVerified: boolean): string | undefined {
  if (!emailVerified || isPrivateRelayEmail(email) || isPlaceholderEmail(email)) return undefined;
  const d = emailDomainOf(email);
  if (d === undefined || isPublicEmailDomain(d)) return undefined;
  return d;
}

/** Whether this address may join a workspace that allows `allowed` (exact domain match). */
export function canJoinByDomain(email: string | null | undefined, emailVerified: boolean, allowed: string[]): boolean {
  const d = joinDomainFor(email, emailVerified);
  return d !== undefined && allowed.some((a) => normalizeEmailDomain(a) === d);
}
